//apps/web/src/components/visitors/PhotoUploader.tsx
import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  CircularProgress,
  Typography,
} from '@mui/material';
import { CameraAlt, Close, FlipCameraAndroid, Delete } from '@mui/icons-material';
import Webcam from 'react-webcam';
import { environment } from '../../config/environment';

interface PhotoUploaderProps {
  currentPhoto?: string;
  onPhotoChange: (photoData: string) => void;
  disabled?: boolean;
}

const PhotoUploader: React.FC<PhotoUploaderProps> = ({ currentPhoto, onPhotoChange, disabled }) => {
  const [openCamera, setOpenCamera] = useState(false);
  const [photoData, setPhotoData] = useState<string | null>(null);
  const [facingMode, setFacingMode] = useState<'user' | 'environment'>('user');
  const [isCapturing, setIsCapturing] = useState(false);
  const webcamRef = useRef<Webcam>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Функция для получения полного URL фото
  const getFullPhotoUrl = (photoName?: string): string | undefined => {
    if (!photoName) return undefined;

    // Если это уже полный URL или base64
    if (photoName.startsWith('http') || photoName.startsWith('data:')) {
      return photoName;
    }

    // Если это имя файла, добавляем базовый URL
    return `${environment.apiBaseUrl}/uploads/visitors/${photoName}`;
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Проверяем размер файла
      if (file.size > 5 * 1024 * 1024) {
        alert('Файл слишком большой. Максимальный размер: 5MB');
        return;
      }

      // Проверяем тип файла
      if (!file.type.startsWith('image/')) {
        alert('Пожалуйста, выберите изображение');
        return;
      }

      convertFileToBase64(file);
    }
  };

  const convertFileToBase64 = (file: File) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result as string;
      setPhotoData(base64String);
      onPhotoChange(base64String);
    };
    reader.readAsDataURL(file);
  };

  const capturePhoto = () => {
    if (webcamRef.current) {
      setIsCapturing(true);
      const imageSrc = webcamRef.current.getScreenshot();
      if (imageSrc) {
        setPhotoData(imageSrc);
        onPhotoChange(imageSrc);
        setTimeout(() => {
          setOpenCamera(false);
          setIsCapturing(false);
        }, 500);
      }
    }
  };

  const toggleCamera = () => {
    setFacingMode((prev) => (prev === 'user' ? 'environment' : 'user'));
  };

  const removePhoto = () => {
    setPhotoData(null);
    onPhotoChange('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const videoConstraints = {
    width: 1280,
    height: 720,
    facingMode: facingMode,
  };

  return (
    <Box>
      <Stack direction="row" spacing={3} alignItems="center">
        <Avatar
          src={photoData || getFullPhotoUrl(currentPhoto)}
          sx={{ width: 100, height: 100, border: '2px solid #e0e0e0' }}
        >
          <Typography variant="h5">{!photoData && !currentPhoto ? 'ФОТО' : null}</Typography>
        </Avatar>

        <Stack spacing={2}>
          <Button
            variant="contained"
            startIcon={<CameraAlt />}
            onClick={() => setOpenCamera(true)}
            disabled={disabled}
            size="small"
          >
            Сделать фото
          </Button>

          <Button
            variant="outlined"
            component="label"
            startIcon={<CameraAlt />}
            disabled={disabled}
            size="small"
          >
            Загрузить фото
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileUpload}
              accept="image/jpeg,image/png,image/gif"
              hidden
            />
          </Button>

          <Typography variant="caption" color="textSecondary">
            Поддерживаются: JPG, PNG, GIF
            <br />
            Максимальный размер: 5MB
          </Typography>

          {(photoData || currentPhoto) && !disabled && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<Delete />}
              onClick={removePhoto}
              size="small"
            >
              Удалить фото
            </Button>
          )}
        </Stack>
      </Stack>

      <Dialog open={openCamera} onClose={() => setOpenCamera(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Сделать фото
          <IconButton
            onClick={() => setOpenCamera(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <Box sx={{ position: 'relative', height: 400 }}>
            {isCapturing && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgba(0,0,0,0.5)',
                  zIndex: 10,
                }}
              >
                <CircularProgress color="primary" />
              </Box>
            )}

            <Webcam
              audio={false}
              ref={webcamRef}
              screenshotFormat="image/jpeg"
              videoConstraints={videoConstraints}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          </Box>
        </DialogContent>

        <DialogActions>
          <Button startIcon={<FlipCameraAndroid />} onClick={toggleCamera}>
            Переключить камеру
          </Button>

          <Button variant="contained" onClick={capturePhoto} disabled={isCapturing}>
            Сделать снимок
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PhotoUploader;
